import { useQuery } from '@apollo/client/react';
import { Alert, Stack } from '@mui/material';
import { AlertTriangle, FileText, Lightbulb, MailWarning } from 'lucide-react';

import { type ListingInsightsQueryVariables } from '@graphql/__generated__/graphql';
import { LISTING_INSIGHTS } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

type CalloutSeverity = 'warning' | 'info';

// Which funnel drill-down a callout belongs under. `null` = no chart section,
// so it stays in the top-level banner above the funnel.
export type CoachSection =
  | 'impressions'
  | 'enquiries'
  | 'buyerLeads'
  | 'offers'
  | null;

export type Callout = {
  id: string;
  severity: CalloutSeverity;
  icon: React.ReactNode;
  message: string;
  section: CoachSection;
};

// "High interest but no viewings" needs enough enquiries to be meaningful.
const INTEREST_THRESHOLD = 5;
// Momentum is "dropping" when this week is at least 35% below the 4-week avg.
const MOMENTUM_DROP_RATIO = 0.65;

// Rule-based coaching tips driven entirely by real CRM data. Each tip turns a
// signal an agent would otherwise have to dig for into a one-line nudge, and is
// tagged with the funnel section it belongs under.
export const useCoachCallouts = (
  lotId: string,
  insightsVars: ListingInsightsQueryVariables,
): Callout[] => {
  const { t } = useLocale();
  const { data } = useQuery(LISTING_INSIGHTS, { variables: insightsVars });

  const callouts: Callout[] = [];

  const unanswered = data?.unanswered.aggregate?.count ?? 0;
  if (unanswered > 0) {
    callouts.push({
      id: 'unanswered',
      severity: 'warning',
      section: 'enquiries',
      icon: <MailWarning size={20} />,
      message: t(
        '{{count}} enquiry has gone unanswered for over 24h — follow up to keep buyers engaged.',
        { count: unanswered },
      ),
    });
  }

  const recentEnquiries = data?.recent_enquiries.aggregate?.count ?? 0;
  const priorEnquiries = data?.prior_enquiries.aggregate?.count ?? 0;
  const priorPerWeek = priorEnquiries / 4;
  if (
    priorPerWeek > 0 &&
    recentEnquiries < priorPerWeek * MOMENTUM_DROP_RATIO
  ) {
    const dropPct = Math.round((1 - recentEnquiries / priorPerWeek) * 100);
    callouts.push({
      id: 'momentum',
      severity: 'warning',
      section: 'enquiries',
      icon: <AlertTriangle size={20} />,
      message: t(
        'Enquiries are down {{percent}}% vs the previous weeks — consider a price or marketing review.',
        { percent: dropPct },
      ),
    });
  }

  const monthEnquiries = data?.month_enquiries.aggregate?.count ?? 0;
  const monthViewings = data?.month_viewings.aggregate?.count ?? 0;
  if (monthEnquiries >= INTEREST_THRESHOLD && monthViewings === 0) {
    callouts.push({
      id: 'no-viewings',
      severity: 'info',
      section: 'enquiries',
      icon: <Lightbulb size={20} />,
      message: t(
        '{{count}} enquiry but no viewings booked in the last 4 weeks — reach out to convert interest into visits.',
        { count: monthEnquiries },
      ),
    });
  }

  const pendingOffers = data?.pending_offers.aggregate?.count ?? 0;
  if (pendingOffers > 0) {
    callouts.push({
      id: 'pending-offers',
      severity: 'info',
      section: 'offers',
      icon: <FileText size={20} />,
      message: t('{{count}} offer is awaiting a response.', {
        count: pendingOffers,
      }),
    });
  }

  return callouts;
};

// Controlled presentation of a list of callouts. Dismissal is owned by the
// parent so the same callout never shows twice (banner vs funnel section) and a
// dismissed callout also clears the funnel step's alert indicator.
export const CoachCallouts = ({
  callouts,
  onDismiss,
}: {
  callouts: Callout[];
  onDismiss: (id: string) => void;
}) => {
  if (callouts.length === 0) {
    return null;
  }
  return (
    <Stack spacing={1}>
      {callouts.map(callout => (
        <Alert
          key={callout.id}
          severity={callout.severity}
          icon={callout.icon}
          onClose={() => onDismiss(callout.id)}
          sx={{
            borderRadius: 2,
            alignItems: 'center',
            '& .MuiAlert-message': { fontSize: '0.875rem' },
          }}
        >
          {callout.message}
        </Alert>
      ))}
    </Stack>
  );
};
